import { useMemo, useCallback } from 'react';
import { useViewPreference, RANGE_MODE_PREFERENCE } from '@atlassian/jira-business-preferences';
import { MONTHS, WEEKS, QUARTERS } from '@atlassian/jira-software-roadmap-timeline-table';
import type { TimelineMode } from '@atlassian/jira-software-roadmap-timeline-table/src/types';
import { getToday } from '@atlassian/jira-software-roadmap-utils/src/utils/dates';
import {
	getTimelineDurationFromDate,
	getTimelineOriginForToday,
	getDefaultTimelinePositionFromDate,
} from '@atlassian/jira-software-roadmap-utils/src/utils/timeline';

const validTimelineModes = new Set<TimelineMode>([WEEKS, MONTHS, QUARTERS]);

const useRangeMode = () => {
	const [rangeModePreference, setRangeModePreference] = useViewPreference(RANGE_MODE_PREFERENCE);

	const setRangeMode = useCallback(
		(newTimelineMode: TimelineMode) => {
			if (newTimelineMode === MONTHS) {
				return setRangeModePreference(undefined);
			}
			return setRangeModePreference(newTimelineMode.toLowerCase());
		},
		[setRangeModePreference],
	);

	const rangeMode: TimelineMode = useMemo(() => {
		if (
			rangeModePreference != null &&
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			validTimelineModes.has(rangeModePreference.toUpperCase() as TimelineMode)
		) {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return rangeModePreference.toUpperCase() as TimelineMode;
		}
		return MONTHS;
	}, [rangeModePreference]);

	return [rangeMode, setRangeMode] as const;
};

export const parseRangeModeQueryParam = (queryParam?: string): TimelineMode =>
	// @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'TimelineMode'. | TS2345 - Argument of type 'string' is not assignable to parameter of type 'TimelineMode'.
	queryParam !== undefined && validTimelineModes.has(queryParam) ? queryParam : MONTHS;

export const useTimelineUI = () => {
	const [rangeModeQueryParam, setRangeMode] = useRangeMode();

	const timelineMode = parseRangeModeQueryParam(rangeModeQueryParam);

	const today = getToday();
	const timelineDuration = getTimelineDurationFromDate(timelineMode, today);
	const timelineOrigin = getTimelineOriginForToday(today, timelineMode);
	const todayPosition = getDefaultTimelinePositionFromDate(today, timelineMode, timelineDuration);

	return [
		{
			today,
			timelineMode,
			timelineDuration,
			timelineOrigin,
			todayPosition,
		},
		{
			setTimelineMode: setRangeMode,
		},
	] as const;
};
